import React from 'react'
import { Section, SectionTitle, SubSection, SubSectionTitle, Text, List, Table, CodeBlock } from '../styles'

export default function DeveloperDashboardSection() {
  return (
    <Section id="developer-dashboard">
      <SectionTitle>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 3h18v2H3V5zm0 4h18v2H3V9zm0 4h18v2H3v-2zm0 4h18v2H3v-2zm0 4h18v2H3v-2z"/>
        </svg>
        Developer Dashboard
      </SectionTitle>
      
      <SubSection id="api-key-management">
        <SubSectionTitle>API Key Management</SubSectionTitle>
        <Text>
          The Developer Dashboard provides comprehensive tools for managing API
          access and monitoring usage:
        </Text>
        <CodeBlock>
{`// API Key Generation Response
{
  "api_key": {
    "id": "key_abc123",
    "key": "sk_live_xyz789...",
    "created_at": "2024-01-20T10:30:00Z",
    "last_used": "2024-01-20T15:45:00Z",
    "permissions": [
      "projects.read",
      "projects.write",
      "agents.deploy",
      "outputs.read"
    ],
    "metadata": {
      "environment": "production",
      "created_by": "wallet_address",
      "description": "Production API Key"
    }
  },
  "rate_limits": {
    "tier": "pro",
    "requests_per_hour": 1000,
    "burst_limit": 1500,
    "concurrent_requests": 50
  },
  "usage_metrics": {
    "current_period": {
      "start": "2024-01-20T00:00:00Z",
      "end": "2024-01-20T23:59:59Z",
      "requests": 450,
      "remaining": 550
    }
  }
}`}
        </CodeBlock>
      </SubSection>

      <SubSection id="usage-monitoring">
        <SubSectionTitle>Usage Monitoring</SubSectionTitle>
        <Text>
          Real-time monitoring and analytics for API usage:
        </Text>
        <Table>
          <thead>
            <tr>
              <th>Metric</th>
              <th>Description</th>
              <th>Visualization</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Request Volume</td>
              <td>Total API requests over time</td>
              <td>Time series graph</td>
            </tr>
            <tr>
              <td>Response Times</td>
              <td>Latency metrics by endpoint</td>
              <td>Histogram</td>
            </tr>
            <tr>
              <td>Error Rates</td>
              <td>Failed requests by type</td>
              <td>Pie chart</td>
            </tr>
            <tr>
              <td>Resource Usage</td>
              <td>Compute/storage utilization</td>
              <td>Gauge chart</td>
            </tr>
          </tbody>
        </Table>
      </SubSection>

      <SubSection id="sdk-integration">
        <SubSectionTitle>SDK Integration</SubSectionTitle>
        <Text>
          Language-specific SDKs for seamless integration:
        </Text>
        <CodeBlock>
{`// Python SDK Example
from ai_agents_platform import Client, Project, Agent

client = Client(api_key="your_api_key")

# Create a new project
project = Project.create(
    name="Website Development",
    description="Create a modern React website",
    agents={
        "coders": {
            "count": 3,
            "capabilities": ["frontend", "react"]
        }
    }
)

# Deploy agents
agents = project.deploy_agents()

# Monitor progress
for event in project.stream_events():
    print(f"Event: {event.type}, Status: {event.status}")

# JavaScript/TypeScript SDK
import { AIAgentsPlatform } from '@ai-agents/sdk';

const client = new AIAgentsPlatform({
  apiKey: 'your_api_key',
});

const project = await client.projects.create({
  name: 'Website Development',
  description: 'Create a modern React website',
  agents: {
    coders: {
      count: 3,
      capabilities: ['frontend', 'react'],
    },
  },
});

// Stream updates
const unsubscribe = project.onUpdate((update) => {
  console.log('Project update:', update);
});

// Rust SDK
use ai_agents_platform::{Client, ProjectConfig, AgentConfig};

let client = Client::new("your_api_key")?;

let project = client.create_project(ProjectConfig {
    name: "Website Development",
    description: "Create a modern React website",
    agents: vec![
        AgentConfig {
            agent_type: "coder",
            count: 3,
            capabilities: vec!["frontend", "react"],
        }
    ],
})?;

// Stream updates
let mut events = project.subscribe_events()?;
while let Some(event) = events.next().await {
    println!("Event received: {:?}", event);
}`}
        </CodeBlock>
      </SubSection>

      <SubSection id="api-explorer">
        <SubSectionTitle>Interactive API Explorer</SubSectionTitle>
        <Text>
          Built-in API explorer for testing endpoints directly from the
          documentation:
        </Text>
        <List>
          <li>
            Features:
            <ul>
              <li>Live endpoint testing with request builder</li>
              <li>Authentication key management</li>
              <li>Request/response history</li>
              <li>Response validation and formatting</li>
              <li>Code generation for multiple languages</li>
            </ul>
          </li>
          <li>
            Testing Tools:
            <ul>
              <li>Request parameter validation</li>
              <li>Response schema validation</li>
              <li>Error simulation options</li>
              <li>Rate limit monitoring</li>
              <li>Performance metrics</li>
            </ul>
          </li>
          <li>
            Documentation:
            <ul>
              <li>OpenAPI/Swagger integration</li>
              <li>Interactive examples</li>
              <li>Schema visualization</li>
              <li>Error code reference</li>
              <li>Best practices guide</li>
            </ul>
          </li>
        </List>
      </SubSection>
    </Section>
  )
}
